/**
 * ssrComposite — SSR OWNERSHIP (render wave 1, 2026-09-17).
 *
 * three's r185 SSR example ADDS the reflection onto the scene colour, and the
 * pipeline copied it. The scene colour already carries the environment's
 * indirect specular (the sky PMREM), so a metal or a puddle reflected twice
 * wherever a screen-space ray hit — `intensity 0.6` was the damping. This is
 * the CPU mirror of the pipeline's TSL: a HIT (the node's alpha is the hit's
 * world distance; 0 on a miss) replaces `SSR_REPLACE` of the scene colour by
 * metalness — a metal's beauty is almost all indirect specular, a dielectric
 * keeps its diffuse and gains only the Fresnel-weighted reflection the node
 * already scales.
 *
 * `SSR_REPLACE` < 1 keeps a little of the IBL for tint and occlusion: the
 * non-stochastic node does not tint its reflection by the albedo.
 */
export const SSR_REPLACE = 0.85;

/** The node's alpha is a hit distance (0 on a miss); anything above the epsilon is a shaded hit. */
export const SSR_HIT_EPS = 1e-4;

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

export function ssrHit(a: number): number { return a > SSR_HIT_EPS ? 1 : 0; }

export function ssrCompositeWeight(a: number, metalness: number): number {
  return ssrHit(a) * clamp01(metalness) * SSR_REPLACE;
}

export function ssrCompositeCpu(
  scene: [number, number, number],
  ssr: [number, number, number, number],
  metalness: number,
): [number, number, number] {
  const w = ssrCompositeWeight(ssr[3], metalness);
  return [scene[0] * (1 - w) + ssr[0], scene[1] * (1 - w) + ssr[1], scene[2] * (1 - w) + ssr[2]];
}
